import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  EmptyState,
  ListGroup,
  ListRow,
  SegmentedControl,
  Skeleton,
  Switch,
  TextField,
} from '@uplink/ui';
import { useState } from 'react';
import { errorText, useTRPC } from '../api.ts';
import { MainActionBar } from '../native-controls.tsx';
import { usePrefs } from '../prefs.tsx';
import { ConfirmButton } from '../servers-ui.tsx';
import { haptic } from '../telegram.ts';

const GROUPS = [
  { id: 'servers', title: 'Серверы' },
  { id: 'domains', title: 'Домены' },
  { id: 'payments', title: 'Платежи' },
  { id: 'services', title: 'Почта и ИИ' },
] as const;

const ACTIONS: Record<string, string> = {
  set_key: 'новый ключ',
  clear_key: 'ключ из .env',
  set_setting: 'изменено',
  clear_setting: 'значение из .env',
};

type Editing =
  | { kind: 'key'; key: string }
  | { kind: 'text' | 'choice'; key: string }
  | { kind: 'confirm'; key: string };

/**
 * Providers and payment systems: keys (never shown, only the last four characters), switches and options.
 * Everything here wins over .env and applies without a restart; a switch that spends money asks first and
 * checks the provider's key. A provider is ready once its key is in and, for clouds, selling is on.
 */
export function AdminProvidersScreen() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const { dateTime } = usePrefs();
  const list = useQuery(trpc.admin.providers.queryOptions());
  const [editing, setEditing] = useState<Editing | null>(null);
  const [value, setValue] = useState('');
  const [done, setDone] = useState<string | null>(null);

  const refresh = () => {
    queryClient.invalidateQueries(trpc.admin.pathFilter());
    queryClient.invalidateQueries(trpc.servers.pathFilter());
  };
  const finish = (text: string) => {
    haptic.success();
    setDone(text);
    setEditing(null);
    setValue('');
    refresh();
  };
  const saveKey = useMutation(
    trpc.admin.setCredential.mutationOptions({
      onSuccess: (r) => finish(r.checked ? `Ключ сохранён и работает: ${r.checked}` : 'Ключ сохранён'),
      onError: () => haptic.error(),
    }),
  );
  const clearKey = useMutation(
    trpc.admin.clearCredential.mutationOptions({
      onSuccess: () => finish('Вернули ключ из .env'),
      onError: () => haptic.error(),
    }),
  );
  const saveSetting = useMutation(
    trpc.admin.setSetting.mutationOptions({
      onSuccess: (r) => finish(r.checked ? `Сохранено: ${r.checked}` : 'Сохранено'),
      onError: () => haptic.error(),
    }),
  );
  const clearSetting = useMutation(
    trpc.admin.clearSetting.mutationOptions({
      onSuccess: () => finish('Вернули значение из .env'),
      onError: () => haptic.error(),
    }),
  );
  const mutations = [saveKey, clearKey, saveSetting, clearSetting];
  const failed = mutations.find((m) => m.isError);
  const open = (next: Editing | null, initial = '') => {
    // The editor sits above the list
    if (next) window.scrollTo({ top: 0, behavior: 'smooth' });
    setEditing(next);
    setValue(initial);
    setDone(null);
    for (const m of mutations) m.reset();
  };

  if (list.isPending) {
    return (
      <main className="ul-screen">
        <h1 className="ul-screen__title">Провайдеры</h1>
        <Skeleton height={320} />
      </main>
    );
  }
  if (list.isError || !list.data.enabled) {
    return (
      <main className="ul-screen">
        <h1 className="ul-screen__title">Провайдеры</h1>
        <EmptyState
          icon="key"
          title={list.isError ? 'Не загрузилось' : 'Нужен SECRETS_KEY'}
          text={
            list.isError
              ? errorText(list.error)
              : 'Ключи хранятся зашифрованными: добавьте SECRETS_KEY в .env на сервере и перезапустите Uplink'
          }
        />
      </main>
    );
  }

  const providers = list.data.providers;
  const keyOf = (key: string) =>
    providers.flatMap((p) => p.keys.map((k) => ({ p, k }))).find((x) => x.k.key === key);
  const settingOf = (key: string) =>
    providers.flatMap((p) => p.settings.map((s) => ({ p, s }))).find((x) => x.s.key === key);
  const labelOf = (key: string) => {
    const hit = keyOf(key) ?? settingOf(key);
    if (!hit) return key;
    return `${hit.p.name} · ${'k' in hit ? hit.k.label : hit.s.label}`;
  };
  const rejected = saveKey.isError && saveKey.error.message.startsWith('Сервис не принял ключ');

  return (
    <main className="ul-screen">
      <h1 className="ul-screen__title">Провайдеры</h1>
      <p className="ul-group__footer">
        Ключи и настройки отсюда заменяют .env и применяются без перезапуска: API сразу, воркер и боты за
        минуту. Чтобы подключить облако, вставьте ключ и включите «Продавать серверы».
      </p>

      {done ? (
        <div className="ul-success-note" role="status">
          {done}
        </div>
      ) : null}

      {editing ? (
        <Editor
          editing={editing}
          title={labelOf(editing.key)}
          value={value}
          onChange={setValue}
          keyView={keyOf(editing.key)?.k}
          setting={settingOf(editing.key)?.s}
          error={failed ? errorText(failed.error) : null}
          busy={mutations.some((m) => m.isPending)}
          rejected={rejected}
          onSaveKey={(force) => saveKey.mutate({ key: editing.key, value: value.trim(), force })}
          onClearKey={() => clearKey.mutate({ key: editing.key })}
          onSaveSetting={(v) => saveSetting.mutate({ key: editing.key, value: v })}
          onClearSetting={() => clearSetting.mutate({ key: editing.key })}
          onCancel={() => open(null)}
        />
      ) : null}

      {GROUPS.map((g) =>
        providers
          .filter((p) => p.group === g.id)
          .map((p, i) => (
            <ListGroup key={p.id} header={i === 0 ? `${g.title} · ${p.name}` : p.name} footer={p.about}>
              <ProviderStatusRow id={p.id} />
              {p.keys.map((k) => (
                <ListRow
                  key={k.key}
                  icon="key"
                  title={k.label}
                  subtitle={k.source === 'none' ? 'Не задан' : `${k.masked} · ${sourceText(k.source)}`}
                  tone={k.source === 'none' ? 'warn' : undefined}
                  chevron
                  onClick={() => open({ kind: 'key', key: k.key })}
                />
              ))}
              {p.settings.map((s) =>
                s.kind === 'toggle' ? (
                  <ListRow
                    key={s.key}
                    icon={s.spends ? 'bolt' : 'settings'}
                    title={s.label}
                    subtitle={s.source === 'admin' ? 'задано в админке' : (s.hint ?? undefined)}
                    trailing={
                      <Switch
                        checked={s.value === '1'}
                        label={`${p.name}: ${s.label}`}
                        onChange={(on) => {
                          if (on && s.spends) open({ kind: 'confirm', key: s.key });
                          else saveSetting.mutate({ key: s.key, value: on ? '1' : '0' });
                        }}
                      />
                    }
                  />
                ) : (
                  <ListRow
                    key={s.key}
                    icon="settings"
                    title={s.label}
                    subtitle={
                      s.value
                        ? `${s.options?.find((o) => o.value === s.value)?.label ?? s.value} · ${sourceText(s.source)}`
                        : 'Не задано'
                    }
                    chevron
                    onClick={() =>
                      open({ kind: s.kind === 'choice' ? 'choice' : 'text', key: s.key }, s.value)
                    }
                  />
                ),
              )}
            </ListGroup>
          )),
      )}

      {list.data.log.length ? (
        <ListGroup header="Последние изменения">
          {list.data.log.map((e) => (
            <ListRow
              key={e.id}
              icon="clock"
              title={labelOf(e.key)}
              subtitle={[
                ACTIONS[e.action] ?? e.action,
                e.detail && e.action === 'set_setting'
                  ? e.detail === '1'
                    ? 'включено'
                    : e.detail === '0'
                      ? 'выключено'
                      : e.detail
                  : e.detail,
                e.username ? `@${e.username}` : e.firstName,
                dateTime(e.at),
              ]
                .filter(Boolean)
                .join(' · ')}
            />
          ))}
        </ListGroup>
      ) : null}
    </main>
  );
}

/** The provider's account right now: balance or whether the key works, plans, customers' servers */
function ProviderStatusRow({ id }: { id: string }) {
  const trpc = useTRPC();
  const status = useQuery({ ...trpc.admin.providerStatus.queryOptions({ id }), staleTime: 60_000 });
  return (
    <ListRow
      icon={status.data?.ok ? 'check' : 'alert'}
      tone={status.data && !status.data.ok ? 'warn' : undefined}
      title={
        status.isPending
          ? 'Проверяем…'
          : status.isError
            ? 'Не проверили'
            : status.data.ok
              ? 'Работает'
              : 'Не работает'
      }
      subtitle={status.isError ? errorText(status.error) : status.data?.text}
      trailing={
        <Button variant="ghost" onClick={() => status.refetch()}>
          Обновить
        </Button>
      }
    />
  );
}

function Editor(props: {
  editing: Editing;
  title: string;
  value: string;
  onChange: (v: string) => void;
  keyView?: { source: string; masked: string | null };
  setting?: {
    kind: string;
    value: string;
    source: string;
    hint?: string;
    options?: { value: string; label: string }[];
  };
  error: string | null;
  busy: boolean;
  rejected: boolean;
  onSaveKey: (force: boolean) => void;
  onClearKey: () => void;
  onSaveSetting: (value: string) => void;
  onClearSetting: () => void;
  onCancel: () => void;
}) {
  const { editing, setting, keyView } = props;
  const errorBlock = props.error ? (
    <div className="ul-error" role="alert">
      {props.error}
    </div>
  ) : null;

  if (editing.kind === 'confirm') {
    return (
      <section className="ul-stack">
        <div className="ul-group__header">{props.title}</div>
        <p className="ul-group__footer">
          Включить? Клиенты смогут заказывать, и это потратит реальные деньги у провайдера. Перед включением
          проверим ключ и покажем баланс.
        </p>
        {errorBlock}
        <ConfirmButton
          variant="secondary"
          loading={props.busy}
          confirmText="Нажмите ещё раз, чтобы включить"
          onConfirm={() => props.onSaveSetting('1')}
        >
          Включить
        </ConfirmButton>
        <Button variant="ghost" onClick={props.onCancel}>
          Отмена
        </Button>
      </section>
    );
  }

  const isKey = editing.kind === 'key';
  const trimmed = props.value.trim();
  const valid = isKey
    ? trimmed.length > 0 && !/\s/.test(trimmed)
    : editing.kind === 'choice'
      ? !!trimmed
      : true;
  return (
    <section className="ul-stack">
      <div className="ul-group__header">{props.title}</div>
      {editing.kind === 'choice' && setting?.options ? (
        <SegmentedControl<string>
          label={props.title}
          value={props.value}
          onChange={props.onChange}
          options={setting.options}
        />
      ) : (
        <TextField
          id="provider-value"
          label={isKey ? 'Новый ключ' : 'Значение'}
          type={isKey ? 'password' : 'text'}
          autoComplete="off"
          value={props.value}
          onChange={props.onChange}
          hint={
            isKey
              ? keyView?.masked
                ? `Сейчас: ${keyView.masked} (${sourceText(keyView.source)})`
                : 'Сейчас не задан'
              : setting?.hint
          }
        />
      )}
      {errorBlock}
      {isKey && props.rejected ? (
        <Button variant="secondary" loading={props.busy} onClick={() => props.onSaveKey(true)}>
          Сохранить без проверки
        </Button>
      ) : null}
      {(isKey ? keyView?.source : setting?.source) === 'admin' ? (
        <Button
          variant="secondary"
          loading={props.busy}
          onClick={isKey ? props.onClearKey : props.onClearSetting}
        >
          Вернуть значение из .env
        </Button>
      ) : null}
      <Button variant="ghost" onClick={props.onCancel}>
        Отмена
      </Button>
      <MainActionBar
        action={{
          text: isKey ? (valid ? 'Проверить и сохранить' : 'Вставьте ключ') : 'Сохранить',
          disabled: !valid,
          loading: props.busy,
          onClick: () => valid && (isKey ? props.onSaveKey(false) : props.onSaveSetting(trimmed)),
        }}
      />
    </section>
  );
}

function sourceText(source: string) {
  return source === 'admin' ? 'из админки' : source === 'env' ? 'из .env' : 'не задан';
}
